import type { MinecraftServerStats } from '../types/statusMonitor';
import type { UpdateAdvisorEntry } from '../types/updateAdvisor';
import { normalizeHostname } from './statusMonitor';

const SEEN_OFFLINE_MS = 180_000; // 3 minutes — matches Status Monitor Minecraft cards

/** Recent backup = still finishing; aligned with Status Monitor Minecraft cards. */
export const BACKUP_IN_PROGRESS_THRESHOLD_MS = 2 * 60 * 1000;
const CONCERN_MS = 24 * 60 * 60 * 1000;
const CRITICAL_MS = 7 * 24 * 60 * 60 * 1000;

export type BackupAgeSeverity = 'ok' | 'concern' | 'critical';

export function getBackupAgeSeverity(msSinceBackup: number): BackupAgeSeverity {
  if (msSinceBackup > CRITICAL_MS) return 'critical';
  if (msSinceBackup > CONCERN_MS) return 'concern';
  return 'ok';
}

export function isMinecraftInstanceOnline(s: MinecraftServerStats, now: number): boolean {
  return now - s.last_seen * 1000 <= SEEN_OFFLINE_MS;
}

function stripHostPort(host: string): string {
  return host.trim().replace(/:\d+$/, '');
}

/** Match advisor row host to status monitor `hostname` (FQDN or short name). */
export function advisorHostMatchesMinecraftHostname(
  minecraftHostname: string,
  advisorServerAddress: string
): boolean {
  const rawM = stripHostPort(minecraftHostname).toLowerCase();
  const rawA = stripHostPort(advisorServerAddress).toLowerCase();
  if (!rawM || !rawA) return false;

  const m = normalizeHostname(rawM);
  const a = normalizeHostname(rawA);

  if (m === a || rawM === rawA) return true;
  if (m.endsWith('.' + a) || a.endsWith('.' + m)) return true;
  if (rawM.endsWith('.' + rawA) || rawA.endsWith('.' + rawM)) return true;

  const firstM = m.split('.')[0] || m;
  const firstA = a.split('.')[0] || a;
  if (firstM && firstA && firstM === firstA) return true;

  if (a.length >= 3 && (m.includes(a) || rawM.includes(rawA))) return true;
  if (m.length >= 3 && (a.includes(m) || rawA.includes(rawM))) return true;
  return false;
}

/** All instances for this pack: try advisor cluster_name and numeric cluster_parent (socket ids vary). */
export function instancesForAdvisorCluster(
  entry: UpdateAdvisorEntry,
  servers: Record<string, MinecraftServerStats>
): MinecraftServerStats[] {
  const name = entry.cluster_name?.trim() ?? '';
  const parentKey = String(entry.cluster_parent);
  const keys = new Set<string>();
  if (name) {
    keys.add(name);
    keys.add(name.toLowerCase());
  }
  if (parentKey) keys.add(parentKey);

  if (keys.size === 0) return [];

  return Object.values(servers).filter((s) => {
    const cid = (s.clusterId ?? '').trim();
    if (!cid) return false;
    const lower = cid.toLowerCase();
    for (const k of keys) {
      if (cid === k || lower === k.toLowerCase()) return true;
    }
    return false;
  });
}

export function instancesForCluster(
  clusterId: string,
  servers: Record<string, MinecraftServerStats>
): MinecraftServerStats[] {
  const id = clusterId.trim();
  if (!id) return [];
  const lower = id.toLowerCase();
  return Object.values(servers).filter((s) => {
    const cid = (s.clusterId ?? '').trim();
    return cid === id || cid.toLowerCase() === lower;
  });
}

function pickNewestSeen(instances: MinecraftServerStats[]): MinecraftServerStats | undefined {
  if (instances.length === 0) return undefined;
  return instances.reduce((best, s) => (s.last_seen > best.last_seen ? s : best));
}

/**
 * Prefer host match (advisor server_address ↔ socket hostname).
 * If none, fall back to any instance in the same cluster (same backup signal as original single-row UI).
 */
export function findMinecraftInstanceForAdvisorEntry(
  entry: UpdateAdvisorEntry,
  servers: Record<string, MinecraftServerStats>,
  now = Date.now()
): MinecraftServerStats | undefined {
  const pool = instancesForAdvisorCluster(entry, servers);
  if (pool.length === 0) return undefined;

  const hostMatches = pool.filter((s) =>
    advisorHostMatchesMinecraftHostname(s.hostname, entry.server_address)
  );
  const narrowed = hostMatches.length > 0 ? hostMatches : pool;

  const online = narrowed.filter((s) => isMinecraftInstanceOnline(s, now));
  const use = online.length > 0 ? online : narrowed;
  return pickNewestSeen(use);
}

export type RegionBackupLineKind =
  | 'loading'
  | 'no_host'
  | 'offline'
  | 'in_progress'
  | 'na'
  | 'age';

export interface RegionBackupLine {
  regionLabel: string;
  kind: RegionBackupLineKind;
  /** Set when kind === 'age' */
  msSinceBackup?: number;
}

export function getRegionBackupLine(
  entry: UpdateAdvisorEntry,
  servers: Record<string, MinecraftServerStats>,
  clusterHasSocketData: boolean,
  now = Date.now()
): RegionBackupLine {
  const regionLabel = (entry.region_name || '—').trim() || '—';
  const cluster = entry.cluster_name?.trim() ?? '';

  if (!cluster) {
    return { regionLabel, kind: 'no_host' };
  }

  if (!clusterHasSocketData) {
    return { regionLabel, kind: 'loading' };
  }

  const inst = findMinecraftInstanceForAdvisorEntry(entry, servers, now);
  if (!inst) {
    return { regionLabel, kind: 'no_host' };
  }

  if (!isMinecraftInstanceOnline(inst, now)) {
    return { regionLabel, kind: 'offline' };
  }

  if (inst.last_backup_taken != null && inst.last_backup_taken > 0) {
    const backupMs = inst.last_backup_taken * 1000;
    const age = now - backupMs;
    if (age >= 0 && age < BACKUP_IN_PROGRESS_THRESHOLD_MS) {
      return { regionLabel, kind: 'in_progress' };
    }
  }

  if (inst.last_backup_taken == null || inst.last_backup_taken <= 0) {
    return { regionLabel, kind: 'na' };
  }

  const msSinceBackup = now - inst.last_backup_taken * 1000;
  return { regionLabel, kind: 'age', msSinceBackup };
}

/** Compact age string aligned with Status Monitor cards (e.g. `<1m`, `2h`, `3d`). */
export function formatBackupAgeCompact(ms: number): string {
  const seconds = Math.floor(ms / 1000);
  if (seconds < 60) return `<1m`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  return `${days}d`;
}
